import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AppTabs } from '../shared/tabs/AppTabs';
import { AppTabPanel } from '../shared/tabs/AppTabPanel';
import {
    ProjectCustomFieldsTabContent,
    ProjectEscalationsTabContent,
    ProjectGroupsTabContent,
    ProjectScheduleTabContent,
    ProjectSlaTabContent,
    ProjectTicketCatalogTabContent,
} from './components';
import { useProjectDetailScreen } from './hooks';

export const ProjectDetailScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { token } = useSelector((state) => state.auth);

    const {
        project,
        isLoading,
        error,
        activeTab,
        isSaving,
        form,
        tabs,
        statusClassMap,
        customerId,
        customerName,
        formatDate,
        setActiveTab,
        handleInputChange,
        handleSave,
    } = useProjectDetailScreen({
        token,
        id,
        navigate,
        location,
    });

    return (
        <>
            <div className="row">
                <div className="col-xxl-4 col-xl-6">
                    <div className="card stretch stretch-full">
                        <div className="card-body">
                            {isLoading && <div className="py-3 text-muted">Cargando proyecto...</div>}

                            {!isLoading && error && (
                                <div className="alert alert-danger py-2" role="alert">{error}</div>
                            )}

                            {!isLoading && !error && project && (
                                <>
                                    <div className="mb-4 text-center">
                                        <div className="wd-150 ht-150 mx-auto mb-3 rounded-circle border border-5 border-gray-3 d-flex align-items-center justify-content-center">
                                            <i className="feather-folder text-primary fs-1"></i>
                                        </div>
                                        <div className="mb-4">
                                            <span className="fs-14 fw-bold d-block">
                                                {customerId ? (
                                                    <Link
                                                        to={`/customer/detail/${customerId}`}
                                                        state={{ customerName: customerName || undefined }}
                                                        className="text-decoration-none"
                                                    >
                                                        {customerName || customerId}
                                                    </Link>
                                                ) : '-'}
                                            </span>
                                            <span className="fs-12 fw-normal text-muted d-block">{project.code || '-'}</span>
                                        </div>
                                        <div className="fs-12 fw-normal text-muted text-center d-flex flex-wrap gap-3 mb-4">
                                            <div className="flex-fill py-3 px-4 rounded-1 d-none d-sm-block border border-dashed border-gray-5">
                                                <h6 className="fs-15 fw-bolder">{project.code || '-'}</h6>
                                                <p className="fs-12 text-muted mb-0">Código</p>
                                            </div>
                                            <div className="flex-fill py-3 px-4 rounded-1 d-none d-sm-block border border-dashed border-gray-5">
                                                <h6 className="fs-15 fw-bolder">
                                                    <span className={`badge ${statusClassMap[project.status] || 'bg-soft-secondary text-secondary'}`}>{project.status || '-'}</span>
                                                </h6>
                                                <p className="fs-12 text-muted mb-0">Estatus</p>
                                            </div>
                                            <div className="flex-fill py-3 px-4 rounded-1 d-none d-sm-block border border-dashed border-gray-5">
                                                <h6 className="fs-15 fw-bolder">{formatDate(project.startDate)}</h6>
                                                <p className="fs-12 text-muted mb-0">Inicio</p>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-xxl-8 col-xl-6 px-xl-3 px-xxl-4">
                    <div className="card border-top-0">
                        <div className="nav nav-tabs pt-0 pb-0">
                            <AppTabs
                                tabs={tabs}
                                activeTab={activeTab}
                                onChange={setActiveTab}
                                navClassName="nav nav-tabs flex-wrap w-100 customers-nav-tabs"
                                itemClassName="nav-item border-top border-end"
                            />
                        </div>

                        <div className="tab-content">
                            <AppTabPanel isActive={activeTab === 'general'} className="p-4">
                                {isLoading && <div className="py-3 text-muted">Cargando detalles...</div>}

                                {!isLoading && !error && project && (
                                    <div className="profile-details mb-1">
                                        <div className="mb-4 d-flex align-items-center justify-content-between">
                                            <h5 className="fw-bold mb-0">Detalles del proyecto:</h5>
                                            <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={isSaving}>
                                                {isSaving ? 'Guardando...' : 'Guardar'}
                                            </button>
                                    </div>

                                        <div className="row g-3 mb-3">
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor="project-name">Nombre</label>
                                                <input id="project-name" name="name" className="form-control" value={form.name} onChange={handleInputChange} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor="project-status">Estatus</label>
                                                <select id="project-status" name="status" className="form-select form-select-sm" value={form.status} onChange={handleInputChange}>
                                                    <option value="ACTIVE">Activo</option>
                                                    <option value="SUSPENDED">Suspendido</option>
                                                    <option value="INACTIVE">Inactivo</option>
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor="project-start-date">Inicio</label>
                                                <input id="project-start-date" type="date" name="startDate" className="form-control" value={form.startDate} onChange={handleInputChange} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor="project-end-date">Fin</label>
                                                <input id="project-end-date" type="date" name="endDate" className="form-control" value={form.endDate} onChange={handleInputChange} />
                                            </div>
                                        </div>

                                        <div className="row g-0 mb-4">
                                            <div className="col-sm-6 text-muted">Creado: {formatDate(project.createdDate)}</div>
                                        </div>
                                    </div>
                                )}
                            </AppTabPanel>

                            <AppTabPanel isActive={activeTab === 'ticket-catalog'} className="p-4">
                                <ProjectTicketCatalogTabContent projectId={id} />
                            </AppTabPanel>

                            <AppTabPanel isActive={activeTab === 'schedule'} className="p-4">
                                <ProjectScheduleTabContent projectId={id} />
                            </AppTabPanel>

                            <AppTabPanel isActive={activeTab === 'sla'} className="p-4">
                                <ProjectSlaTabContent projectId={id} />
                            </AppTabPanel>

                            <AppTabPanel isActive={activeTab === 'groups'} className="p-4">
                                <ProjectGroupsTabContent projectId={id} />
                            </AppTabPanel>

                            <AppTabPanel isActive={activeTab === 'custom-fields'} className="p-4">
                                <ProjectCustomFieldsTabContent projectId={id} />
                            </AppTabPanel>

                            <AppTabPanel isActive={activeTab === 'escalations'} className="p-4">
                                <ProjectEscalationsTabContent projectId={id} />
                            </AppTabPanel>
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                draggable
                theme="light"
            />
        </>
    );
};
